import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getVoteById,
  addQuestionToVote,
  updateQuestion,
  deleteQuestion,
  addChoiceToQuestion,
  deleteChoice,
} from "../../../services/voteServices";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { FaSave, FaTimes } from "react-icons/fa";

const VoteQuestionsModal = ({ isOpen, onClose, voteId, voteTitle }) => {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ question_text: "", type: "single" });
  const [editingId, setEditingId] = useState(null);
  const [choiceForms, setChoiceForms] = useState({});

  useEffect(() => {
    if (isOpen && voteId) {
      fetchQuestions();
    }
  }, [isOpen, voteId]);

  const fetchQuestions = async () => {
    try {
      const vote = await getVoteById(voteId);
      setQuestions(vote?.questions || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des questions");
    }
  };

  const handleQuillChange = (value) => {
    setForm((prev) => ({ ...prev, question_text: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.question_text || form.question_text.trim() === "<p><br></p>") {
      return toast.warn("Le texte de la question est requis");
    }

    try {
      if (editingId) {
        await updateQuestion(editingId, form);
        toast.success("Question modifiée");
      } else {
        await addQuestionToVote(voteId, form);
        toast.success("Question ajoutée");
      }
      setForm({ question_text: "", type: "single" });
      setEditingId(null);
      fetchQuestions();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (q) => {
    setForm({ question_text: q.question_text, type: q.type });
    setEditingId(q.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await deleteQuestion(id);
      toast.success("Question supprimée");
      fetchQuestions();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleChoiceChange = (questionId, value) => {
    setChoiceForms((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleAddChoice = async (questionId) => {
    const text = choiceForms[questionId]?.trim();
    if (!text) return;

    try {
      await addChoiceToQuestion(questionId, { choice_text: text });
      toast.success("Réponse ajoutée");
      setChoiceForms((prev) => ({ ...prev, [questionId]: "" }));
      fetchQuestions();
    } catch (err) {
      toast.error("Erreur ajout réponse");
    }
  };

  const handleDeleteChoice = async (choiceId) => {
    try {
      await deleteChoice(choiceId);
      toast.success("Réponse supprimée");
      fetchQuestions();
    } catch (err) {
      toast.error("Erreur suppression réponse");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="modal-box bg-white dark:bg-slate-200 max-w-3xl w-full p-4 rounded-lg shadow-lg overflow-y-auto max-h-[90vh]">
        <h3 className="text-xl font-bold uppercase p-3 mb-4 text-center text-white bg-primary rounded-md shadow">
          Questions du vote :{" "}
          <span className="italic font-light">{voteTitle}</span>
        </h3>

        {/* Formulaire de question */}
        <div className="flex flex-col gap-2 mb-4">
          <ReactQuill
            value={form.question_text}
            onChange={handleQuillChange}
            theme="snow"
            placeholder="Écrire la question ici (texte enrichi ou image)..."
          />
          <div className="flex items-center gap-2">
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="select select-bordered w-full sm:w-auto"
            >
              <option value="single">Réponse unique</option>
              <option value="multiple">Réponses multiples</option>
              <option value="open">Réponse libre</option>
            </select>
            <Button
              text={editingId ? "Modifier" : "Ajouter"}
              icon={FaSave}
              onClick={handleSubmit}
              className="btn-primary gap-2"
            />
          </div>
        </div>

        {/* Tableau des questions */}
        <table className="table-auto w-full border border-gray-300 mb-4 text-sm dark:text-black">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="p-2 border text-white">Questions</th>
              <th className="p-2 border text-white">Type</th>
              <th className="p-2 border text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {questions.length > 0 ? (
              questions.map((q) => (
                <React.Fragment key={q.id}>
                  <tr>
                    <td colSpan="3" className="bg-slate-300 h-[1px]"></td>
                  </tr>
                  <tr>
                    <td className="p-2 border">
                      <div
                        dangerouslySetInnerHTML={{ __html: q.question_text }}
                      />
                    </td>
                    <td className="p-2 border capitalize">
                      {q.type === "single"
                        ? "Réponse unique"
                        : q.type === "multiple"
                        ? "Réponses multiples"
                        : "Réponse libre"}
                    </td>
                    <td className="p-2 border flex justify-center gap-2">
                      <ButtonEdit onClick={() => handleEdit(q)} />
                      <ButtonDelete onClick={() => handleDelete(q.id)} />
                    </td>
                  </tr>

                  {(q.type === "single" || q.type === "multiple") && (
                    <>
                      {q.choices?.map((choice) => (
                        <tr key={choice.id} className="bg-gray-50">
                          <td className="p-2 border pl-6" colSpan={2}>
                            ⏹️ {choice.choice_text}
                          </td>
                          <td className="p-2 border flex justify-center gap-2">
                            <ButtonDelete
                              onClick={() => handleDeleteChoice(choice.id)}
                            />
                          </td>
                        </tr>
                      ))}

                      <tr className="bg-gray-100">
                        <td colSpan={2} className="p-2 border">
                          <input
                            type="text"
                            value={choiceForms[q.id] || ""}
                            onChange={(e) =>
                              handleChoiceChange(q.id, e.target.value)
                            }
                            placeholder="Ajouter un choix"
                            className="input input-bordered w-full"
                          />
                        </td>
                        <td className="p-2 border text-center">
                          <Button
                            text="Ajouter"
                            onClick={() => handleAddChoice(q.id)}
                            className="btn-primary text-xs"
                          />
                        </td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center p-3">
                  Aucune question enregistrée pour ce vote.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex justify-center mt-2 gap-2">
          <Button
            text="Fermer"
            icon={FaTimes}
            onClick={onClose}
            className="gap-2"
          />
        </div>
      </div>
    </div>
  );
};

export default VoteQuestionsModal;
